import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { toast } from 'react-toastify';
import HandleChange from '../../../../../Helpers/HandleChange';
import routes from '../../../../../Routes/routes';
import { MODEL_DATA, STEPS_CREATE_WORK_SHIFT } from '../Model/Models';

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azapata@ibl.mx
 *
 * Shared hook for the work shift create/update flows. Mirrors the ShiftAccess
 * CommonShiftAccessApp: centralizes form state, the client-side selection lists
 * (schedules buffer / users / departments), their "select for delete" buffers,
 * step navigation and validation. Consumed by CreateWorkShiftApp (create) and
 * UpdateWorkShiftApp (update).
 *
 * @returns {Object} Shared state and handlers.
 */
const CommonWorkShiftApp = () => {

    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { state } = location;
    const [modified, setModified] = useState(false);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const [item, setItem] = useState({ ...MODEL_DATA });
    const [disabled, setDisabled] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);

    // Client-side selection lists + their delete buffers (one per section).
    // Schedules are the work shift's zone/day/time entries (not a resource-mode
    // catalog); buffered in create and submitted with the combined create.
    const [schedules, setSchedules] = useState([]);
    const [selectDeleteSchedules, setSelectDeleteSchedules] = useState([]);

    const [departments, setDepartments] = useState([]);
    const [selectDeleteDepartments, setSelectDeleteDepartments] = useState([]);

    const [user, setUser] = useState([]);
    const [selectDeleteUser, setSelectDeleteUser] = useState([]);

    // Stepper state (create flow).
    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(STEPS_CREATE_WORK_SHIFT.WORK_SHIFT.INDEX);
    const [searchElement, setSearchElement] = useState('');
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    /**
     * Sets the active stepper step (create flow).
     * @param {number} step - Step index.
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
        setSearchElement("");
    }

    /**
     * Final validation before create/update.
     * @returns {boolean}
     */
    const validate = () => {
        if (!item.isValid(schedules)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return false;
        }
        setEmptyFields(false);
        return true;
    }

    /**
     * Per-step validation used by the create stepper "next" buttons. Only the
     * first step (name + schedules) is required.
     * @param {number} step - Active step index.
     * @returns {boolean}
     */
    const handleValidateSteps = (step) => {
        if (step === STEPS_CREATE_WORK_SHIFT.WORK_SHIFT.INDEX && !item.isValid(schedules)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return false;
        }
        setSearchElement("");
        setEmptyFields(false);
        return true;
    }

    /**
     * Clears only the resources owned by a given step.
     * @param {number} step - Step index.
     */
    const handleClearStep = (step) => {
        const clearMap = {
            [STEPS_CREATE_WORK_SHIFT.WORK_SHIFT.INDEX]: () => {
                setItem(prev => ({ ...prev, name: '' }));
                setSearchElement('');
                setSchedules([]);
                setSelectDeleteSchedules([]);
                setModified(true);
                setEmptyFields(true);
            },
            [STEPS_CREATE_WORK_SHIFT.CUSTOM_ACCESS.INDEX]: () => {
                setSearchElement('');
                setUser([]);
                setModified(true);
            },
            [STEPS_CREATE_WORK_SHIFT.DEPARTMENTS.INDEX]: () => {
                setSearchElement('');
                setDepartments([]);
                setModified(true);
            },
        };
        clearMap[step]?.();
    }

    /**
     * Navigates back to the work shift list.
     */
    const handleOnBack = () => {
        navigate(routes.workshift.list);
    }

    return {
        id,
        t,
        DEV,
        permission,
        dispatch,
        navigate,
        state,

        item, setItem,
        disabled, setDisabled,
        emptyFields, setEmptyFields,
        handleChange,

        schedules, setSchedules,
        selectDeleteSchedules, setSelectDeleteSchedules,

        departments, setDepartments,
        selectDeleteDepartments, setSelectDeleteDepartments,

        user, setUser,
        selectDeleteUser, setSelectDeleteUser,

        steps, setSteps,
        activeStep, setActiveStep,
        handleStepChange,
        searchElement,
        setSearchElement,
        validate,
        handleValidateSteps,
        handleClearStep,
        handleOnBack,
        modified, setModified
    }
}

export default CommonWorkShiftApp
